import { clsx } from 'clsx';
import { QuestionCategorySummary } from '../../services/backend/questionCategories';
import { downloadCSV } from '../../util/files';
import ErrorDisplay from '../common/ErrorDisplay';
import Button from '../common/forms/Button';
import Icon from '../common/Icon';

interface AICodingSummaryTabProps {
  questionId: number;
  summaryData: QuestionCategorySummary[] | undefined;
  isLoadingSummary: boolean;
  summaryError: Error | null;
}

const AICodingSummaryTab = ({
  questionId,
  summaryData,
  isLoadingSummary,
  summaryError,
}: AICodingSummaryTabProps): JSX.Element => {
  const downloadSummaryCSV = () => {
    const data = (summaryData || []).map((s) => ({
      Category: s.name,
      'Response Count': s.count,
      'Response %': `${s.percentage}%`,
    }));
    downloadCSV({
      data,
      filename: `summary_${questionId}.csv`,
      errorLabel: 'summary',
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-end mb-6">
        <Button
          disabled={!summaryData || summaryData.length === 0}
          hierarchy="secondary-gray"
          icon={<Icon id="download" />}
          onClick={downloadSummaryCSV}
          size="md"
        >
          Download CSV
        </Button>
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="border border-gray-d-200 rounded-lg bg-white">
          {isLoadingSummary ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-d-500">Loading summary...</div>
            </div>
          ) : summaryError ? (
            <div className="flex items-center justify-center py-12 px-6">
              <ErrorDisplay message="Failed to load summary" />
            </div>
          ) : !summaryData || summaryData.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-d-500">No summary data found</div>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[400px]">
              <table className="w-full">
                <thead className="bg-gray-d-50 sticky top-0 z-10 border-b border-gray-d-200">
                  <tr>
                    <th className="py-3 px-6 text-left text-sm font-semibold text-gray-d-600">
                      Category
                    </th>
                    <th className="py-3 px-6 text-right text-sm font-semibold text-gray-d-600 w-36">
                      Response Count
                    </th>
                    <th className="py-3 px-6 text-right text-sm font-semibold text-gray-d-600 w-32">
                      Response %
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summaryData.map((item, index) => (
                    <tr
                      key={index}
                      className={clsx(
                        'hover:bg-gray-d-200 transition-colors border-b border-gray-d-100 last:border-0',
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-d-50',
                        item.name === 'Total' && 'font-bold bg-gray-d-100',
                      )}
                    >
                      <td className="py-4 px-6 text-sm text-gray-d-900 align-top">
                        {item.name}
                      </td>
                      <td className="py-4 px-6 text-right text-sm text-gray-d-900 align-top">
                        {item.count}
                      </td>
                      {item.name !== 'Total' && (
                        <td className="py-4 px-6 text-right text-sm text-gray-d-900 align-top">
                          {item.percentage}%
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AICodingSummaryTab;
